import { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { VISITIQ_CATALOG_ID } from '~/components/AIInsightsUI/a2ui/operationsEnvelope';
import { pulseTopicOptionLabel } from '~/utils/pulse';

import {
  CardField,
  CardFieldOption,
  formatFiltersValue,
  isBooleanField,
  isStructuredField,
  matchbackSelectionLabels,
} from './confirmationFields';

const A2UI_VERSION = 'v0.9';
export const SUBMIT_ACTION_NAME = 'confirm_mutation';
export const CANCEL_ACTION_NAME = 'cancel_mutation';

interface DisplayFieldArgs {
  id: string;
  label: string;
  path: string;
  value: unknown;
}

interface FieldComponent {
  component: Record<string, unknown>;
  dataModelValue?: unknown;
  path?: string;
  /** Bind a data-model value the component needs to render, without submitting the field. */
  readOnly?: boolean;
}

interface ActionLabels {
  confirm: string;
  cancel: string;
}

interface SurfaceArgs {
  fields: CardField[];
  surfaceId: string;
  labels: ActionLabels;
}

export const buildConfirmationSurface = ({ fields, surfaceId, labels }: SurfaceArgs): A2uiOperation[] | null => {
  if (fields.length === 0) return null;

  const dataModel: Record<string, unknown> = {};
  const submitContext: Record<string, unknown> = {};
  const childIds: string[] = [];
  const fieldComponents: Record<string, unknown>[] = [];

  fields.forEach((field, index) => {
    const id = `field-${index}`;
    const built = buildFieldComponent(field, id);
    fieldComponents.push(built.component);
    childIds.push(id);
    if (built.path !== undefined) {
      dataModel[field.name] = built.dataModelValue;
      // A read-only field stays out of the submit context, so the gate backfills it from the
      // proposed value rather than from what the card rendered.
      if (!built.readOnly) submitContext[field.name] = { path: built.path };
    }
  });

  const components: Record<string, unknown>[] = [
    { id: 'root', component: 'Column', children: [...childIds, 'actions'] },
    ...fieldComponents,
    { id: 'actions', component: 'Row', justify: 'end', children: ['cancel', 'submit'] },
    {
      id: 'cancel',
      component: 'Button',
      child: 'cancel-text',
      action: { event: { name: CANCEL_ACTION_NAME, context: {} } },
    },
    { id: 'cancel-text', component: 'Text', text: labels.cancel },
    {
      id: 'submit',
      component: 'Button',
      variant: 'primary',
      child: 'submit-text',
      action: { event: { name: SUBMIT_ACTION_NAME, context: submitContext } },
    },
    { id: 'submit-text', component: 'Text', text: labels.confirm },
  ];

  return [
    { version: A2UI_VERSION, createSurface: { surfaceId, catalogId: VISITIQ_CATALOG_ID } },
    { version: A2UI_VERSION, updateDataModel: { surfaceId, path: '/', value: dataModel } },
    { version: A2UI_VERSION, updateComponents: { surfaceId, components } },
  ];
};

export const surfaceHasCancelAction = (operations: A2uiOperation[]): boolean =>
  operations.some((operation) => readSurfaceComponents(operation).some(isCancelComponent));

interface LocalizeArgs {
  operations: A2uiOperation[];
  labels: ActionLabels;
  fields: CardField[];
}

export const localizeSurface = ({ operations, labels, fields }: LocalizeArgs): A2uiOperation[] => {
  const actionTextIds = new Map<string, string>();
  operations.forEach((operation) =>
    readSurfaceComponents(operation).forEach((component) => {
      const actionName = readActionName(component);
      const childId = component.child;
      if (typeof childId !== 'string') return;
      if (actionName === SUBMIT_ACTION_NAME) actionTextIds.set(childId, labels.confirm);
      if (actionName === CANCEL_ACTION_NAME) actionTextIds.set(childId, labels.cancel);
    }),
  );
  const captionsByPath = new Map<string, string>();
  const captionsByName = new Map<string, string>();
  const optionLabelsByPath = new Map<string, Map<string, string>>();
  for (const field of fields) {
    const caption = fieldCaption(field);
    captionsByPath.set(`/${field.name}`, caption);
    captionsByName.set(field.name, field.fixed ? field.label : caption);
    if (field.enumOptions) {
      optionLabelsByPath.set(`/${field.name}`, new Map(field.enumOptions.map(({ value, label }) => [value, label])));
    }
  }
  if (actionTextIds.size === 0 && captionsByPath.size === 0) return operations;

  return operations.map((operation) => {
    const update = asRecord(operation.updateComponents);
    const components = readSurfaceComponents(operation);
    if (!update || components.length === 0) return operation;
    return {
      ...operation,
      updateComponents: {
        ...update,
        components: components.map((component) =>
          localizeOptions(
            localizeComponent(component, actionTextIds, captionsByPath, captionsByName),
            optionLabelsByPath,
          ),
        ),
      },
    };
  });
};

const localizeComponent = (
  component: Record<string, unknown>,
  actionTextIds: Map<string, string>,
  captionsByPath: Map<string, string>,
  captionsByName: Map<string, string>,
): Record<string, unknown> => {
  const actionText = typeof component.id === 'string' ? actionTextIds.get(component.id) : undefined;
  if (actionText !== undefined) return { ...component, text: actionText };

  // Unbound field lines (fixed values, read-only structured values) carry their field name and the
  // value half of their text instead of a binding, so the caption is the only part re-rendered.
  const localizedText = localizeUnboundFieldText(component, captionsByName);
  if (localizedText !== undefined) return { ...component, text: localizedText };

  // An unbound display component (a display-only `VIQMatchingRules`) carries its field name next to its label
  const namedCaption = typeof component.fieldName === 'string' ? captionsByName.get(component.fieldName) : undefined;
  if (namedCaption !== undefined && 'label' in component) return { ...component, label: namedCaption };

  const path = asRecord(component.value)?.path;
  const caption = typeof path === 'string' ? captionsByPath.get(path) : undefined;
  if (caption === undefined) return component;
  const captionKey = 'title' in component ? 'title' : 'label';
  return { ...component, [captionKey]: caption };
};

// The agent lists an enum input's raw values as its option labels; show the product's copy instead.
const localizeOptions = (
  component: Record<string, unknown>,
  optionLabelsByPath: Map<string, Map<string, string>>,
): Record<string, unknown> => {
  const path = asRecord(component.value)?.path;
  const optionLabels = typeof path === 'string' ? optionLabelsByPath.get(path) : undefined;
  if (!optionLabels || !Array.isArray(component.options)) return component;
  return {
    ...component,
    options: component.options.map((option) => {
      const value = asRecord(option)?.value;
      const label = typeof value === 'string' ? optionLabels.get(value) : undefined;
      return label === undefined ? option : { ...asRecord(option), label };
    }),
  };
};

const localizeUnboundFieldText = (
  component: Record<string, unknown>,
  captionsByName: Map<string, string>,
): string | undefined => {
  const { fieldName, fieldValueText } = component;
  if (typeof fieldName !== 'string' || typeof fieldValueText !== 'string') return undefined;
  const caption = captionsByName.get(fieldName);
  return caption === undefined ? undefined : `${caption}: ${fieldValueText}`;
};

const readSurfaceComponents = (operation: A2uiOperation): Record<string, unknown>[] => {
  const update = asRecord(operation.updateComponents);
  const components = update?.components;
  return Array.isArray(components) ? components.filter(isRecord) : [];
};

const isCancelComponent = (component: Record<string, unknown>): boolean =>
  readActionName(component) === CANCEL_ACTION_NAME;

const readActionName = (component: Record<string, unknown>): string | null => {
  const name = asRecord(asRecord(component.action)?.event)?.name;
  return typeof name === 'string' ? name : null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fieldCaption = (field: CardField): string => field.label + (field.required ? ' *' : '');

const buildFieldComponent = (field: CardField, id: string): FieldComponent => {
  const path = `/${field.name}`;
  const label = fieldCaption(field);

  if (field.fixed) {
    // Unbound, so the card cannot submit a different value; the gate keeps the model's anyway.
    return { component: { id, component: 'Text', text: `${field.label}: ${formatFixedValue(field.value)}` } };
  }
  if (field.sensitive) {
    return { component: { id, component: 'VIQSecretInput', label, value: { path } }, dataModelValue: '', path };
  }
  if (isBooleanField(field)) {
    return {
      component: { id, component: 'VIQSwitch', title: label, value: { path } },
      dataModelValue: !!field.value,
      path,
    };
  }
  const displayBuilder = DISPLAY_FIELD_BUILDERS[field.display ?? ''] ?? DISPLAY_FIELD_BUILDERS[field.name];
  if (displayBuilder) return displayBuilder({ id, label, path, value: field.value });
  if (field.enumOptions?.length)
    return buildSingleChoiceComponent({ id, label, path, value: field.value }, field.enumOptions);
  if (isStringArray(field.value) || field.stringArray) {
    const value = Array.isArray(field.value) ? field.value : [];
    return { component: { id, component: 'VIQMultiTagInput', label, value: { path } }, dataModelValue: value, path };
  }
  if (isStructuredField(field)) {
    return { component: { id, component: 'Text', text: `${label}: ${JSON.stringify(field.value)}` } };
  }
  const isNumber = field.schemaType === 'integer' || field.schemaType === 'number';
  return {
    component: { id, component: 'TextField', label, value: { path }, ...(isNumber ? { variant: 'number' } : {}) },
    dataModelValue: field.value == null ? '' : String(field.value),
    path,
  };
};

const buildPulseTopicsComponent = ({ id, label, path, value }: DisplayFieldArgs): FieldComponent => {
  const topicIds = isStringArray(value) ? value : [];
  return {
    component: {
      id,
      component: 'ChoicePicker',
      label,
      variant: 'multipleSelection',
      options: topicIds.map((topicId) => ({ label: pulseTopicOptionLabel(topicId), value: topicId })),
      value: { path },
    },
    dataModelValue: topicIds,
    path,
  };
};

/**
 * A single-value enum input as a single-choice dropdown. Mirrors the agent's enum branch in
 * `_build_confirmation_field` (a2ui/surfaces.py), which lists the raw values that `localizeSurface` relabels.
 */
const buildSingleChoiceComponent = (
  { id, label, path, value }: DisplayFieldArgs,
  options: CardFieldOption[],
): FieldComponent => ({
  component: { id, component: 'ChoicePicker', label, variant: 'mutuallyExclusive', options, value: { path } },
  dataModelValue: options.some((option) => option.value === value) ? [value] : [],
  path,
});

const buildFiltersComponent = ({ id, label, value }: DisplayFieldArgs): FieldComponent => ({
  component: { id, component: 'Text', text: `${label}: ${formatFiltersValue(value)}` },
});

/**
 * A Matchback base or comparison list as chips, each naming an audience and its ranges.
 *
 * Read-only, so the proposed value — audience ids included — reaches the tool untouched. Each
 * option therefore carries its own label as its value; an audience id there would leak into the
 * surface.
 *
 * Mirrors the agent's `_build_matchback_selection_field` (a2ui/surfaces.py).
 */
const buildMatchbackSelectionComponent = ({ id, label, path, value }: DisplayFieldArgs): FieldComponent => {
  const labels = matchbackSelectionLabels(value);
  if (labels.length === 0) {
    return { component: { id, component: 'Text', text: `${label}: None` } };
  }
  return {
    component: {
      id,
      component: 'ChoicePicker',
      label,
      variant: 'multipleSelection',
      options: labels.map((text) => ({ label: text, value: text })),
      value: { path },
    },
    dataModelValue: labels,
    path,
    readOnly: true,
  };
};

/**
 * Customer List matching rules as a display-only `VIQMatchingRules`, from the submitted value. Unbound, like a
 * Matchback selection: the mapping (column indexes included) reaches the tool untouched, and a change goes through the
 * mapping form. Mirrors the agent's `_build_customer_list_matching_field` (a2ui/surfaces.py).
 */
const buildCustomerListMatchingComponent = ({ id, label, value }: DisplayFieldArgs): FieldComponent => ({
  component: {
    id,
    component: 'VIQMatchingRules',
    label,
    displayOnly: true,
    value: typeof value === 'string' ? value : '',
  },
});

/**
 * Kept Customer List columns as a display-only `VIQCustomFields`, from the submitted value. Unbound, like the matching
 * rules. Mirrors the agent's `_build_customer_list_kept_columns_field` (a2ui/surfaces.py).
 */
const buildCustomerListKeptColumnsComponent = ({ id, label, value }: DisplayFieldArgs): FieldComponent => ({
  component: {
    id,
    component: 'VIQCustomFields',
    label,
    displayOnly: true,
    value: typeof value === 'string' ? value : '',
  },
});

const DISPLAY_FIELD_BUILDERS: Record<string, (args: DisplayFieldArgs) => FieldComponent> = {
  pulseTopics: buildPulseTopicsComponent,
  filters: buildFiltersComponent,
  matchbackSelection: buildMatchbackSelectionComponent,
  customerListMatching: buildCustomerListMatchingComponent,
  customerListKeptColumns: buildCustomerListKeptColumnsComponent,
};

const formatFixedValue = (value: unknown): string =>
  value !== null && typeof value === 'object' ? JSON.stringify(value) : `${value as string}`;

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');
